<template>
    <div class="agent-tab">
        <div class="agent-tab-layout">
            <!-- Sol taraf - Agent Ayarları (%75) -->
            <div class="agent-settings-container">
                <!-- 1. Kısım: Toolbar -->
                <div class="agent-toolbar">
                    <div class="toolbar-left">
                        <div class="model-selector">
                            <LLMConfiguration :agent="agent" @update="handleLLMConfigUpdate" />
                        </div>
                        <div class="voice-configuration">
                            <VoiceConfiguration :agent="agent" @update="handleVoiceConfigUpdate" />
                        </div>
                        <div class="language-selector">
                            <LanguagePicker
                                v-model="agent.language"
                                :languages="languageOptions"
                                :title="$t('voiceagent.selectLanguage')"
                                icon="translate"
                                @change="handleLanguageChange"
                            />
                        </div>
                    </div>
                    <div class="toolbar-right">
                        <PermissionController :permissions="['voice-agent.update']">
                            <supsis-button primary @click="saveAgent">{{ $t('voiceagent.save') }}</supsis-button>
                        </PermissionController>
                    </div>
                </div>

                <!-- 2. Kısım: Prompt Yazma Editörü -->
                <div class="prompt-editor-container">
                    <div class="prompt-header">
                        <div class="prompt-title">
                            <h4>{{ $t('voiceagent.voiceAgentPrompt') }}</h4>
                            <span class="prompt-subtitle"
                                >{{ $t('voiceagent.defineAgentBehaviorAndSpeech') }}</span
                            >
                        </div>
                        <div class="prompt-status" v-if="isSaving">
                            <i class="material-icons spin">autorenew</i> {{ $t('voiceagent.saving') }}
                        </div>
                        <div class="prompt-status saved" v-else-if="agent.llm && promptSaved">
                            <i class="material-icons">check_circle</i> {{ $t('voiceagent.saved') }}
                        </div>
                        <PermissionController :permissions="['voice-agent.update']">
                            <supsis-button primary @click="savePrompt" :loading="isSaving">
                                <i class="material-icons">save</i> {{ $t('voiceagent.saveChanges') }}
                            </supsis-button>
                        </PermissionController>
                    </div>

                    <div class="prompt-editor-wrapper">
                        <a-textarea
                            v-if="agent.llm"
                            v-model="agent.llm.general_prompt"
                            :rows="20"
                            :placeholder="$t('voiceagent.writeAgentPromptHere')"
                            class="prompt-editor"
                            @input="handlePromptChange"
                            ref="promptEditor"
                        ></a-textarea>
                    </div>
                    <div class="prompt-actions">
                        <PermissionController :permissions="['voice-agent.update']">
                            <supsis-button type="default" @click="showVariableModal">
                                <i class="material-icons">add</i> {{ $t('voiceagent.addVariable') }}
                            </supsis-button>
                        </PermissionController>
                    </div>
                </div>

                <!-- 3. Kısım: Welcome Mesajı Seçici -->
                <div class="welcome-message-selector">
                    <h4>{{ $t('voiceagent.welcomeMessage') }}</h4>
                    <CustomPicker
                        v-model="welcomeMessageType"
                        :items="welcomeMessageOptions"
                        idKey="value"
                        labelKey="title"
                        :title="$t('voiceagent.selectWelcomeMessageType')"
                        icon="message"
                        @input="handleWelcomeMessageChange"
                    />

                    <div v-if="welcomeMessageType === 'ai_defined'" class="custom-welcome-message">
                        <a-textarea
                            v-model="customWelcomeMessage"
                            :rows="3"
                            :placeholder="$t('voiceagent.enterCustomWelcomeMessage')"
                        ></a-textarea>
                    </div>
                </div>
            </div>

            <!-- Sağ taraf - Test Kısmı (%25) -->
            <div class="agent-test-container">
                <div class="test-panel">
                    <h3>{{ $t('voiceagent.agentTest') }}</h3>
                    <div class="test-tabs">
                        <div
                            class="test-tab"
                            :class="{ active: testTab === 'call' }"
                            @click="testTab = 'call'"
                        >
                            <i class="material-icons">call</i>
                            {{ $t('voiceagent.callTest') }}
                        </div>
                        <div
                            class="test-tab"
                            :class="{ active: testTab === 'chat' }"
                            @click="testTab = 'chat'"
                        >
                            <i class="material-icons">chat</i>
                            {{ $t('voiceagent.chatTest') }}
                        </div>
                    </div>
                    <div class="test-content">
                        <RetellAudioTest
                            v-if="testTab === 'call'"
                            :agent="agent"
                            @callStarted="handleCallStarted"
                            @callEnded="handleCallEnded"
                        />
                        <RetellChatTest
                            v-if="testTab === 'chat'"
                            :agent="agent"
                            @chatStarted="handleChatStarted"
                            @chatEnded="handleChatEnded"
                        />
                    </div>
                </div>
            </div>
        </div>
        <!-- Değişken Seçici Bileşeni -->
        <variable-selector
            :visible="variableModalVisible"
            @close="closeVariableModal"
            @insert-variable="insertVariable"
        />
    </div>
</template>

<script>
import CustomPicker from "../../../../../../../components/pickers/CustomPicker.vue";
import LanguagePicker from "../../../../../../../components/pickers/LanguagePicker.vue";
import RetellAudioTest from "./RetellAudioTest.vue";
import RetellChatTest from "./RetellChatTest.vue";
import VoiceConfiguration from "../../../components/VoiceConfiguration.vue";
import LLMConfiguration from "../../../components/LLMConfiguration.vue";
import VariableSelector from "../../../components/VariableSelector.vue";
import SupsisButton from "../../../../../Settings/components/SupsisButton.vue";
import _ from "lodash";
import { VOICE_AGENT_API } from "../../../../../../../api/voiceAgent";
import log2 from "../../../../../../../utils/log2";
import PermissionController from "../../../../../../../components/permission-controller/PermissionController";

const log = log2("[VoiceAgent.AgentTab]");

export default {
    name: "AgentTab",
    components: {
        LanguagePicker,
        RetellAudioTest,
        RetellChatTest,
        VoiceConfiguration,
        LLMConfiguration,
        VariableSelector,
        SupsisButton,
        CustomPicker
    },
    props: {
        agent: {
            type: Object,
            /**
             * {"agent_id":"agent_51a02f438b5d59319f03615a07","last_modification_timestamp":1744274918454,"voice_id":"custom_voice_b61eafcfc9f69443be9536be1e","voice_model":"eleven_turbo_v2_5","voice_temperature":0.8,"voice_speed":1.02,"volume":1,"enable_backchannel":true,"backchannel_frequency":0.5,"backchannel_words":["evet","hı hı"],"interruption_sensitivity":0.85,"ambient_sound":"coffee-shop","agent_name":"Supsis Agent-Giden Bilgi Arama Senaryosu (beyza))","response_engine":{"type":"retell-llm","llm_id":"llm_c14e769d25db92c080441bf2c28a","version":0},"responsiveness":1,"language":"tr-TR","opt_out_sensitive_data_storage":false,"normalize_for_speech":true,"max_call_duration_ms":3600000,"voicemail_detection_timeout_ms":30000,"version":0,"is_published":false,"post_call_analysis_model":"gpt-4o-mini"}
             */
            default: () => null,
        },
    },
    data() {
        return {
            isCallingActive: false,
            isChatActive: false,
            testTab: "call",
            welcomeMessageTimeout: null,
            isSaving: false,
            promptSaved: false,
            welcomeMessageType: "ai_dynamic",
            customWelcomeMessage: "",
            /** Aynı ajanda deep watch ile karşılama metnini sunucudan tekrar yazmayı engellemek için (git-gel flicker) */
            lastWelcomeHydrateAgentId: null,
            testLogs: [],

            // Değişken modal ayarları
            variableModalVisible: false,

            // Varsayılan sistem değişkenleri ve Kullanıcı tanımlı değişkenler VariableSelector bileşenine taşındı

            languageOptions: [
                {
                    id: "en-US",
                    name: "English (US)",
                    countryCode: "us",
                    description: "US English",
                },
                {
                    id: "es-ES",
                    name: "Spanish (Spain)",
                    countryCode: "es",
                    description: "Spanish (Spain)",
                },
                {
                    id: "es-419",
                    name: "Spanish (Latin America)",
                    countryCode: "mx",
                    description: "Spanish (Latin America)",
                },
                {
                    id: "en-IN",
                    name: "English (India)",
                    countryCode: "in",
                    description: "Indian English",
                },
                {
                    id: "en-GB",
                    name: "English (UK)",
                    countryCode: "gb",
                    description: "British English",
                },
                {
                    id: "en-AU",
                    name: "English (Australia)",
                    countryCode: "au",
                    description: "Australian English",
                },
                {
                    id: "en-NZ",
                    name: "English (New Zealand)",
                    countryCode: "nz",
                    description: "New Zealand English",
                },
                {
                    id: "fr-FR",
                    name: "French",
                    countryCode: "fr",
                    description: "French",
                },
                {
                    id: "zh-CN",
                    name: "Chinese",
                    countryCode: "cn",
                    description: "Chinese",
                },
                {
                    id: "de-DE",
                    name: "German",
                    countryCode: "de",
                    description: "German",
                },
                {
                    id: "hi-IN",
                    name: "Hindi",
                    countryCode: "in",
                    description: "Hindi",
                },
                {
                    id: "ja-JP",
                    name: "Japanese",
                    countryCode: "jp",
                    description: "Japanese",
                },
                {
                    id: "pt-PT",
                    name: "Portuguese (Portugal)",
                    countryCode: "pt",
                    description: "Portuguese (Portugal)",
                },
                {
                    id: "pt-BR",
                    name: "Portuguese (Brazil)",
                    countryCode: "br",
                    description: "Portuguese (Brazil)",
                },
                {
                    id: "ru-RU",
                    name: "Russian",
                    countryCode: "ru",
                    description: "Russian",
                },
                {
                    id: "it-IT",
                    name: "Italian",
                    countryCode: "it",
                    description: "Italian",
                },
                {
                    id: "ko-KR",
                    name: "Korean",
                    countryCode: "kr",
                    description: "Korean",
                },
                {
                    id: "nl-NL",
                    name: "Dutch",
                    countryCode: "nl",
                    description: "Dutch",
                },
                {
                    id: "nl-BE",
                    name: "Dutch (Belgium)",
                    countryCode: "be",
                    description: "Dutch (Belgium)",
                },
                {
                    id: "pl-PL",
                    name: "Polish",
                    countryCode: "pl",
                    description: "Polish",
                },
                {
                    id: "tr-TR",
                    name: "Turkish",
                    countryCode: "tr",
                    description: "Turkish",
                },
                {
                    id: "vi-VN",
                    name: "Vietnamese",
                    countryCode: "vn",
                    description: "Vietnamese",
                },
                {
                    id: "ro-RO",
                    name: "Romanian",
                    countryCode: "ro",
                    description: "Romanian",
                },
                {
                    id: "da-DK",
                    name: "Danish",
                    countryCode: "dk",
                    description: "Danish",
                },
                {
                    id: "fi-FI",
                    name: "Finnish",
                    countryCode: "fi",
                    description: "Finnish",
                },
                {
                    id: "el-GR",
                    name: "Greek",
                    countryCode: "gr",
                    description: "Greek",
                },
                {
                    id: "id-ID",
                    name: "Indonesian",
                    countryCode: "id",
                    description: "Indonesian",
                },
                {
                    id: "no-NO",
                    name: "Norwegian",
                    countryCode: "no",
                    description: "Norwegian",
                },
                {
                    id: "sk-SK",
                    name: "Slovak",
                    countryCode: "sk",
                    description: "Slovak",
                },
                {
                    id: "sv-SE",
                    name: "Swedish",
                    countryCode: "se",
                    description: "Swedish",
                },
                {
                    id: "bg-BG",
                    name: "Bulgarian",
                    countryCode: "bg",
                    description: "Bulgarian",
                },
                {
                    id: "hu-HU",
                    name: "Hungarian",
                    countryCode: "hu",
                    description: "Hungarian",
                },
                {
                    id: "ms-MY",
                    name: "Malay",
                    countryCode: "my",
                    description: "Malay",
                },
                {
                    id: "ca-ES",
                    name: "Catalan",
                    countryCode: "es",
                    description: "Catalan",
                },
                {
                    id: "th-TH",
                    name: "Thai",
                    countryCode: "th",
                    description: "Thai",
                },
                {
                    id: "ar-SA",
                    name: "Arabic (Saudi Arabia)",
                    countryCode: "sa",
                    description: "Arabic (Saudi Arabia)",
                },
                {
                    id: "af-ZA",
                    name: "Afrikaans (South Africa)",
                    countryCode: "za",
                    description: "Afrikaans (South Africa)",
                },
                {
                    id: "az-AZ",
                    name: "Azerbaijani (Azerbaijan)",
                    countryCode: "az",
                    description: "Azerbaijani (Azerbaijan)",
                },
                {
                    id: "bs-BA",
                    name: "Bosnian (Bosnia and Herzegovina)",
                    countryCode: "ba",
                    description: "Bosnian (Bosnia and Herzegovina)",
                },
                {
                    id: "cy-GB",
                    name: "Welsh (Wales)",
                    countryCode: "gb-wls",
                    description: "Welsh (Wales)",
                },
                {
                    id: "fa-IR",
                    name: "Persian (Iran)",
                    countryCode: "ir",
                    description: "Persian (Iran)",
                },
                {
                    id: "fil-PH",
                    name: "Filipino (Philippines)",
                    countryCode: "ph",
                    description: "Filipino (Philippines)",
                },
                {
                    id: "gl-ES",
                    name: "Galician (Spain)",
                    countryCode: "es",
                    description: "Galician (Spain)",
                },
                {
                    id: "he-IL",
                    name: "Hebrew (Israel)",
                    countryCode: "il",
                    description: "Hebrew (Israel)",
                },
                {
                    id: "hr-HR",
                    name: "Croatian (Croatia)",
                    countryCode: "hr",
                    description: "Croatian (Croatia)",
                },
                {
                    id: "hy-AM",
                    name: "Armenian (Armenia)",
                    countryCode: "am",
                    description: "Armenian (Armenia)",
                },
                {
                    id: "is-IS",
                    name: "Icelandic (Iceland)",
                    countryCode: "is",
                    description: "Icelandic (Iceland)",
                },
                {
                    id: "kk-KZ",
                    name: "Kazakh (Kazakhstan)",
                    countryCode: "kz",
                    description: "Kazakh (Kazakhstan)",
                },
                {
                    id: "kn-IN",
                    name: "Kannada (India)",
                    countryCode: "in",
                    description: "Kannada (India)",
                },
                {
                    id: "mk-MK",
                    name: "Macedonian (North Macedonia)",
                    countryCode: "mk",
                    description: "Macedonian (North Macedonia)",
                },
                {
                    id: "mr-IN",
                    name: "Marathi (India)",
                    countryCode: "in",
                    description: "Marathi (India)",
                },
                {
                    id: "ne-NP",
                    name: "Nepali (Nepal)",
                    countryCode: "np",
                    description: "Nepali (Nepal)",
                },
                {
                    id: "sl-SI",
                    name: "Slovenian (Slovenia)",
                    countryCode: "si",
                    description: "Slovenian (Slovenia)",
                },
                {
                    id: "sr-RS",
                    name: "Serbian (Serbia)",
                    countryCode: "rs",
                    description: "Serbian (Serbia)",
                },
                {
                    id: "sw-KE",
                    name: "Swahili (Kenya)",
                    countryCode: "ke",
                    description: "Swahili (Kenya)",
                },
                {
                    id: "ta-IN",
                    name: "Tamil (India)",
                    countryCode: "in",
                    description: "Tamil (India)",
                },
                {
                    id: "ur-IN",
                    name: "Urdu (India)",
                    countryCode: "in",
                    description: "Urdu (India)",
                },
                {
                    id: "multi",
                    name: "Multilingual",
                    countryCode: null,
                    icon: "language",
                    description: "",
                },
            ],
        };
    },
    computed: {
        welcomeMessageOptions() {
            return [
                {
                    value: "user_initiates",
                    title: this.$t("voiceagent.welcomeModeUserInitiates"),
                },
                {
                    value: "ai_dynamic",
                    title: this.$t("voiceagent.welcomeModeAiDynamic"),
                },
                {
                    value: "ai_defined",
                    title: this.$t("voiceagent.welcomeModeAiDefined"),
                },
            ];
        },
    },
    methods: {
        handleCallStarted() {
            this.isCallingActive = true;
        },
        handleCallEnded() {
            this.isCallingActive = false;
        },
        handleChatStarted() {
            this.isChatActive = true;
        },
        handleChatEnded() {
            this.isChatActive = false;
        },
        handleLLMConfigUpdate(llmData) {
            log("LLM konfigürasyonu güncellendi:", llmData);

            if (!this.agent.llm) return;

            // Agent.llm nesnesinde değerleri güncelle
            Object.keys(llmData).forEach((key) => {
                this.agent.llm[key] = llmData[key];
            });

            // LLM güncelleme API çağrısı
            this.updateLLM(llmData);
        },
        handleVoiceConfigUpdate(voiceData) {
            log("Ses konfigürasyonu güncellendi:", voiceData);

            // Agent nesnesi üzerinde değerleri güncelle
            Object.keys(voiceData).forEach((key) => {
                this.agent[key] = voiceData[key];
            });

            // Agent güncelleme API çağrısı
            this.$emit("update", voiceData);
        },
        handleLanguageChange(value) {
            if(!PermissionController.check(["voice-agent.update"])) {
                this.$message.error(this.$t("voiceagent.insufficientPermission"));
                return;
            }
            log("Dil değişti:", value);

            this.agent.language = value;
            this.testLogs.push(`Dil değiştirildi: ${value}`);
            this.updateAgentLanguage(value);
        },
        handlePromptChange() {
            // Prompt değiştiğinde kaydedildi durumunu sıfırla
            this.promptSaved = false;
        },
        handleWelcomeMessageChange(value) {
            log("Welcome message türü değişti:", value);
            this.welcomeMessageType = value;

            // Welcome message tipine göre begin_message değerini ayarla
            if (!this.agent.llm) return;

            let beginMessage = null;

            if (value === "user_initiates") {
                beginMessage = "";
            } else if (value === "ai_dynamic") {
                beginMessage = null;
            } else if (value === "ai_defined") {
                // Eğer daha önce ai_defined modundaysa, mevcut mesajı koru
                // Değilse varsayılan değeri kullan
                beginMessage = this.customWelcomeMessage || this.$t("voiceagent.defaultWelcomePhrase");
                this.customWelcomeMessage = beginMessage;
            }

            // LLM'i güncelle
            this.updateLLM({ begin_message: beginMessage });
        },
        updateLLM(llmData = {}, isManualSave = false) {
            if (!this.agent.llm) {
                this.testLogs.push("LLM güncellenemedi: LLM bilgisi eksik");
                return;
            }

            this.isSaving = true;

            // LLM güncellenecek veriler
            log("LLM verisi güncelleniyor:", llmData);
            this.testLogs.push(`LLM güncelleniyor...`);

            // LLM güncelleme API çağrısı (promise döndürülür - saveAgent senkron kayıt için bekleyebilsin)
            return VOICE_AGENT_API.llms
                .update(this.agent.response_engine.llm_id, llmData)
                .then((response) => {
                    this.testLogs.push(`LLM başarıyla güncellendi`);

                    // Manuel kaydetme ise başarı mesajı göster ve promptSaved'i true yap
                    if (isManualSave) {
                        this.promptSaved = true;
                        this.$message.success(this.$t("voiceagent.promptSaveSuccess"));
                    }

                    // Agent nesnesini de güncelleyelim
                    this.$emit("llm-updated", response);
                })
                .catch((error) => {
                    this.testLogs.push(`LLM güncellenirken hata oluştu: ${error.message || this.$t("voiceagent.unknownError")}`);
                    console.error("LLM güncellenirken hata:", error);

                    // Manuel kaydetme ise hata mesajı göster
                    if (isManualSave) {
                        this.$message.error(this.$t("voiceagent.promptSaveFailed"));
                    }
                    return Promise.reject(error);
                })
                .finally(() => {
                    this.isSaving = false;
                });
        },
        updateAgentLanguage(languageValue) {
            const agentData = {
                language: languageValue,
            };

            log("Agent dil bilgisi güncelleniyor:", agentData);
            this.testLogs.push(`Agent dil bilgisi güncelleniyor...`);

            // Update işlemini üst bileşene bildirelim
            this.$emit("update", agentData);
        },
        savePrompt() {
            if (!this.agent.llm) {
                this.$message.warning(this.$t("voiceagent.llmConfigMissing"));
                return;
            }

            if (!PermissionController.check(["voice-agent.update"])) {
                this.$message.error(this.$t("voiceagent.insufficientPermission"));
                return;
            }

            this.promptSaved = false;

            const promptData = {
                general_prompt: this.agent.llm.general_prompt || ""
            };

            log("Prompt manuel olarak kaydediliyor:", promptData);
            this.testLogs.push("Prompt manuel olarak kaydediliyor...");

            // Mevcut updateLLM metodunu kullan ve başarı durumunu takip et
            this.updateLLM(promptData, true); // true parametresi manuel kaydetme olduğunu belirtir
        },
        async saveAgent() {
            this.isSaving = true;

            // Önce mevcut prompt ve begin_message'ı LLM'e kaydet (üstteki Kaydet ile prompt kaybı olmasın)
            const llmData = {};
            if (this.agent.llm) {
                llmData.general_prompt = this.agent.llm.general_prompt || "";
                if (this.welcomeMessageType === "ai_defined") {
                    llmData.begin_message = this.customWelcomeMessage;
                }
            }
            if (Object.keys(llmData).length > 0) {
                try {
                    await this.updateLLM(llmData, false);
                } catch (e) {
                    this.isSaving = false;
                    return;
                }
            }

            // Agent özelliklerini (dil vb.) güncelle
            const agentData = {
                language: this.agent.language,
            };

            log("Agent verisi güncelleniyor:", agentData);
            this.testLogs.push(`Agent güncelleniyor...`);

            this.$emit("update", agentData);

            setTimeout(() => {
                this.isSaving = false;
            }, 500);
        },
        testAudio() {
            // Audio test işlemi
            this.testLogs.push("Audio test başlatıldı...");
            this.testLogs.push(`Seçili dil: ${this.agent.language}`);

            if (this.agent.llm?.general_prompt) {
                // Kısa bir prompt özeti oluştur
                const promptSummary =
                    this.agent.llm.general_prompt.length > 50
                        ? this.agent.llm.general_prompt.substring(0, 50) + "..."
                        : this.agent.llm.general_prompt;

                this.testLogs.push(`Prompt: ${promptSummary}`);
            } else {
                this.testLogs.push(`Prompt tanımlanmamış`);
            }
        },
        showVariableModal() {
            this.variableModalVisible = true;
        },

        closeVariableModal() {
            this.variableModalVisible = false;
        },

        insertVariable(variable) {
            console.log("insertVariable", {
                variable,
                promptEditor: this.$refs.promptEditor,
                textarea: this.$refs.promptEditor?.$el?.querySelector("textarea"),
            });

            if (!this.$refs.promptEditor) return;

            const textarea = this.$refs.promptEditor.$el;
            if (!textarea) return;

            const startPos = textarea.selectionStart;
            const endPos = textarea.selectionEnd;
            const currentValue = this.agent.llm.general_prompt || "";

            // Değişkeni imlecin olduğu pozisyona veya seçili metni değiştirerek ekle
            const newValue = currentValue.substring(0, startPos) + variable.name + currentValue.substring(endPos);

            // Prompt'u güncelle
            this.agent.llm.general_prompt = newValue;

            // İmleci değişkenin sonrasına taşı
            this.$nextTick(() => {
                const newCursorPos = startPos + variable.name.length;
                textarea.focus();
                textarea.setSelectionRange(newCursorPos, newCursorPos);
            });

            // Modal'ı kapat
            this.closeVariableModal();
        },
    },
    watch: {
        agent: {
            handler(newValue) {
                log("Agent verisi değişti:", newValue);
                if (!newValue) {
                    return;
                }
                if (!newValue.llm) {
                    this.welcomeMessageType = "ai_dynamic";
                    return;
                }
                var bm = newValue.llm.begin_message;
                var agentId = newValue.agent_id;
                var isNewAgentContext = agentId !== this.lastWelcomeHydrateAgentId;

                if (isNewAgentContext) {
                    this.lastWelcomeHydrateAgentId = agentId;
                    if (bm === "") {
                        this.welcomeMessageType = "user_initiates";
                    } else if (bm === null) {
                        this.welcomeMessageType = "ai_dynamic";
                    } else {
                        this.welcomeMessageType = "ai_defined";
                        this.customWelcomeMessage = bm || "";
                    }
                    return;
                }

                // Aynı ajan: yalnızca seçici modunu begin_message şekline göre güncelle; textarea'yı ezme
                if (bm === "") {
                    this.welcomeMessageType = "user_initiates";
                } else if (bm && bm.length > 0) {
                    this.welcomeMessageType = "ai_defined";
                } else {
                    this.welcomeMessageType = "ai_dynamic";
                }
            },
            deep: true,
        },
        customWelcomeMessage: {
            handler(newValue) {
                // ai_defined modunda customWelcomeMessage değiştiğinde begin_message'ı güncelle
                if (this.welcomeMessageType === "ai_defined" && this.agent.llm) {
                    // Debounce kullanarak sürekli güncellemeden kaçınalım
                    clearTimeout(this.welcomeMessageTimeout);
                    this.welcomeMessageTimeout = setTimeout(() => {
                        this.updateLLM({ begin_message: newValue });
                    }, 2000);
                }
            },
        },
    },
    created() {
        var multiOpt = this.languageOptions.filter(function (o) {
            return o.id === "multi";
        })[0];
        if (multiOpt) {
            multiOpt.description = this.$t("voiceagent.multilingualOptionDescription");
        }
        // Eğer agent verisi varsa, formu bu verilerle dolduralım
        if (this.agent) {
            log("Agent verisi:", this.agent);
            log("Agent verisi prompt:", this.agent.llm?.general_prompt);

            // Begin message'a göre welcomeMessageType belirleme
            if (this.agent.llm) {
                if (this.agent.llm.begin_message === "") {
                    this.welcomeMessageType = "user_initiates";
                } else if (this.agent.llm.begin_message === null) {
                    this.welcomeMessageType = "ai_dynamic";
                } else {
                    this.welcomeMessageType = "ai_defined";
                    this.customWelcomeMessage = this.agent.llm.begin_message || "";
                }
            } else {
                this.welcomeMessageType = "ai_dynamic";
            }
            this.lastWelcomeHydrateAgentId = this.agent.agent_id;
        }
    },
};
</script>

<style scoped lang="scss">
.agent-tab {
    padding: 20px 0;

    .agent-tab-layout {
        display: flex;
        gap: 20px;

        .agent-settings-container {
            flex: 3; // %75
            display: flex;
            flex-direction: column;
            gap: 20px;

            .agent-toolbar {
                display: flex;
                justify-content: space-between;
                align-items: center;
                padding: 10px;
                background-color: #f9f9f9;
                border-radius: 4px;
                border: 1px solid #e8e8e8;

                .toolbar-left {
                    display: flex;
                    gap: 15px;
                    align-items: center;

                    .flag-icon {
                        margin-right: 5px;
                    }

                    .model-selector {
                        min-width: 200px;
                        margin-right: 10px;
                    }

                    .voice-configuration {
                        min-width: 200px;
                    }
                }
            }

            .prompt-editor-container {
                flex-grow: 1;
                display: flex;
                flex-direction: column;
                border: 1px solid #e8e8e8;
                border-radius: 4px;
                background-color: #fff;
                padding: 15px;

                .prompt-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 15px;

                    .prompt-title {
                        h4 {
                            margin: 0;
                            font-weight: 500;
                        }

                        .prompt-subtitle {
                            color: #666;
                            font-size: 0.9em;
                        }
                    }

                    .prompt-status {
                        display: flex;
                        align-items: center;
                        color: #1890ff;
                        font-size: 0.9em;

                        &.saved {
                            color: #52c41a;
                        }

                        i {
                            margin-right: 5px;
                            font-size: 18px;

                            &.spin {
                                animation: spin 1.5s infinite linear;
                            }
                        }

                        @keyframes spin {
                            0% {
                                transform: rotate(0deg);
                            }
                            100% {
                                transform: rotate(360deg);
                            }
                        }
                    }
                }

                .prompt-editor-wrapper {
                    display: flex;
                    flex-grow: 1;

                    .prompt-editor {
                        width: 100%;
                        height: 100%;
                        min-height: 300px;
                        font-family: "Monaco", "Menlo", "Consolas", monospace;
                        resize: none;
                        border-radius: 4px;
                    }
                }

                .prompt-actions {
                    display: flex;
                    justify-content: flex-end;
                    margin-top: 10px;

                    .supsis-button {
                        margin-left: 10px;
                    }
                }
            }

            .welcome-message-selector {
                padding: 15px;
                background-color: #f9f9f9;
                border-radius: 4px;
                border: 1px solid #e8e8e8;

                h4 {
                    margin-top: 0;
                    margin-bottom: 15px;
                    font-weight: 500;
                }

                .welcome-message-option {
                    display: block;
                    margin-bottom: 10px;
                }

                .custom-welcome-message {
                    margin-top: 15px;
                }
            }
        }

        .agent-test-container {
            flex: 1; // %25

            .test-panel {
                padding: 15px;
                background-color: #f9f9f9;
                border-radius: 4px;
                border: 1px solid #e8e8e8;
                height: 100%;

                h3 {
                    margin-top: 0;
                    margin-bottom: 15px;
                    font-weight: 500;
                }

                .test-tabs {
                    display: flex;
                    border-bottom: 1px solid #e8e8e8;
                    margin-bottom: 15px;

                    .test-tab {
                        display: flex;
                        align-items: center;
                        gap: 6px;
                        padding: 10px 16px;
                        cursor: pointer;
                        color: #666;
                        font-size: 14px;
                        transition: all 0.2s;
                        border-bottom: 2px solid transparent;
                        margin-bottom: -1px;

                        .material-icons {
                            font-size: 18px;
                        }

                        &:hover {
                            color: #1890ff;
                        }

                        &.active {
                            color: #1890ff;
                            border-bottom-color: #1890ff;
                            font-weight: 500;
                        }
                    }
                }

                .test-content {
                    min-height: 300px;
                }

                .test-actions {
                    display: flex;
                    flex-direction: column;
                    gap: 10px;

                    .test-button {
                        display: flex;
                        justify-content: center;
                        align-items: center;
                        gap: 5px;

                        .material-icons {
                            font-size: 18px;
                        }
                    }

                    .test-logs {
                        margin-top: 20px;
                        padding: 10px;
                        background-color: #fff;
                        border: 1px solid #e8e8e8;
                        border-radius: 4px;
                        min-height: 200px;
                        max-height: 300px;
                        overflow-y: auto;

                        .empty-logs {
                            color: #999;
                            text-align: center;
                            font-style: italic;
                        }

                        .log-entries {
                            .log-entry {
                                padding: 5px 0;
                                border-bottom: 1px solid #f0f0f0;

                                &:last-child {
                                    border-bottom: none;
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}
</style>
